import React, { useCallback, useState, useImperativeHandle, useRef, forwardRef } from 'react';
import { useDropzone } from 'react-dropzone';
import styled from 'styled-components';

const UploadContainer = styled.div`
    padding: 20px;
    border: 2px dashed #007bff;
    border-radius: 10px;
    text-align: center;
    width: 290px;
    cursor: ${props => (props.disabled ? 'not-allowed' : 'pointer')};
    background-color: ${props => (props.disabled ? '#f0f0f0' : '#f9f9f9')};
    transition: border-color 0.3s ease, background-color 0.3s ease;
    
    &:hover {
        border-color: ${props => (props.disabled ? '#ccc' : '#0056b3')};
        background-color: ${props => (props.disabled ? '#f0f0f0' : '#e8f0fe')};
    }
`;

const FileList = styled.ul`
    list-style-type: none;
    padding: 0;
    width: 450px;
    margin-top: 10px;
    max-height: 150px;
    overflow-y: auto;
    border: 1px solid #ccc;
    border-radius: 5px;
    background-color: #fff;
    box-shadow: 0 2px 5px rgba(0, 0, 0, 0.1);
`;

const FileItem = styled.li`
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 10px;
    border-bottom: 1px solid #eee;

    &:last-child {
        border-bottom: none;
    }

    &:hover {
        background-color: #f1f1f1;
    }
`;

const RemoveButton = styled.button`
    background-color: #ff4d4d;
    color: white;
    border: none;
    border-radius: 5px;
    padding: 5px 10px;
    cursor: pointer;
    transition: background-color 0.3s ease;

    &:hover {
        background-color: #ff1a1a;
    }
`;

const MAX_FILE_SIZE = 50 * 1024 * 1024; // 50 MB

const UploadComponent = forwardRef(({ onFilesChange, disabled = false }, ref) => {
    const [uploadedFilesVisita, setUploadedFilesVisita] = useState([]);
    const [fileError, setFileError] = useState('');

    const onDrop = useCallback((acceptedFiles, fileRejections) => {
        if (disabled) {
            setFileError('Não é possível adicionar arquivos enquanto o envio está em andamento.');
            return;
        }

        setFileError(''); // limpa erros anteriores

        if (fileRejections.length > 0) {
            fileRejections.forEach(rejection => {
                rejection.errors.forEach(err => {
                    if (err.code === 'file-too-large') {
                        setFileError(`O arquivo "${rejection.file.name}" excede o tamanho máximo de 50 MB.`);
                    }
                });
            });
            return;
        }

        const validFiles = acceptedFiles.filter(file =>
            ['image/png', 'image/jpeg', 'video/mp4', 'video/quicktime'].includes(file.type)
        );

        if (validFiles.length > 0) {
            setUploadedFilesVisita(prevFiles => {
                const newFiles = [...prevFiles, ...validFiles];
                if (newFiles.length > 5) {
                    setFileError('Número máximo de arquivos atingido. Você só pode colocar 5 arquivos por vez.');
                    return prevFiles;
                }
                onFilesChange(newFiles);
                return newFiles;
            });
        }
    }, [onFilesChange, disabled]);

    const { getRootProps, getInputProps } = useDropzone({
        onDrop,
        maxSize: MAX_FILE_SIZE,
    });

    // 🔑 função exposta ao pai
    const clearFiles = () => {
        setUploadedFilesVisita([]);
        onFilesChange([]);
    };

    useImperativeHandle(ref, () => ({
        clearFiles,
    }));

    const handleRemoveFile = (fileName) => {
        setUploadedFilesVisita(prevFiles => {
            const updatedFiles = prevFiles.filter(file => file.name !== fileName);
            onFilesChange(updatedFiles);
            return updatedFiles;
        });
    };

    return (
        <>
            <UploadContainer {...getRootProps()} disabled={disabled}>
                <input {...getInputProps()} />
                <p>Clique ou arraste um vídeo ou imagem para carregar.</p>
            </UploadContainer>
            <small><b>Obs: Máximo de 5 arquivos de 50MB</b></small>
            {fileError && <p style={{ color: 'red' }}>{fileError}</p>}
            {uploadedFilesVisita.length > 0 && (
                <FileList>
                    {uploadedFilesVisita.map(file => (
                        <FileItem key={file.path || file.name}>
                            {file.name} ({(file.size / (1024 * 1024)).toFixed(2)} MB)
                            <RemoveButton onClick={() => handleRemoveFile(file.name)}>Remover</RemoveButton>
                        </FileItem>
                    ))}
                </FileList>
            )}
        </>
    );
});

export default UploadComponent;
